import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/base/Button';
import SectionCard from '@/components/base/SectionCard';
import { useRole } from '@/context/RoleContext';
import { marketFilterLabel } from '@/utils/markets';
import { api } from '@/utils/api';
import {
  campaignCsv,
  campaignInsight,
  campaignKpis,
  creatorCsv,
  creatorInsight,
  creatorKpis,
  downloadCsv,
  type ReportPeriod,
} from '@/utils/report';
import { reportPeriods, reportTabs } from '@/mocks/reports';
import type {
  CampaignReportRow,
  CreatorPerformanceRow,
  ExportDataset,
  ExportFormat,
} from '@/types/portal';
import ReportToolbar from './components/ReportToolbar';
import ReportKpiRow from './components/ReportKpiRow';
import CampaignReportTable from './components/CampaignReportTable';
import CreatorPerformanceTable from './components/CreatorPerformanceTable';
import ReportInsightsPanel from './components/ReportInsightsPanel';
import ExportModal from './components/ExportModal';
import ReportPrintView from './components/ReportPrintView';

export default function Reports() {
  const navigate = useNavigate();
  const { can } = useRole();
  const canExport = can('reports.export');

  const [tab, setTab] = useState('campaign');
  const [period, setPeriod] = useState<ReportPeriod>('6m');
  const [markets, setMarkets] = useState<string[]>([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [printSections, setPrintSections] = useState<string[]>(['campaign', 'creator']);
  const [notice, setNotice] = useState('');
  const [generatedAt] = useState(() =>
    new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
  );

  const [report, setReport] = useState<{ campaigns: CampaignReportRow[]; creators: CreatorPerformanceRow[] }>({
    campaigns: [],
    creators: [],
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // The period is applied on the server (campaigns created in it, as Laravel does).
  useEffect(() => {
    setLoading(true);
    api<{ campaigns: CampaignReportRow[]; creators: CreatorPerformanceRow[] }>(`/reports?period=${period}`)
      .then((data) => {
        setReport(data);
        setLoadError('');
      })
      .catch((error: Error) => setLoadError(error.message))
      .finally(() => setLoading(false));
  }, [period]);

  const campaignRows = useMemo(
    () =>
      report.campaigns.filter(
        (row) => markets.length === 0 || markets.includes(row.market),
      ),
    [report, markets],
  );
  const creatorRows = useMemo(
    () =>
      report.creators.filter(
        (row) => markets.length === 0 || markets.includes(row.market),
      ),
    [report, markets],
  );

  const kpis = useMemo(() => {
    if (tab === 'campaign') return campaignKpis(campaignRows);
    return creatorKpis(creatorRows);
  }, [tab, campaignRows, creatorRows]);

  const periodLabel = reportPeriods.find((option) => option.id === period)?.label ?? 'Last 6 months';
  const currentTabLabel = reportTabs.find((option) => option.id === tab)?.label ?? 'Report';
  const insight = useMemo(
    () => (tab === 'campaign' ? campaignInsight(campaignRows) : creatorInsight(creatorRows)),
    [tab, campaignRows, creatorRows],
  );

  const buildCurrentCsv = () => {
    if (tab === 'campaign') return campaignCsv(campaignRows);
    return creatorCsv(creatorRows);
  };

  const runExport = (format: ExportFormat, dataset: ExportDataset) => {
    const stamp = new Date().toISOString().slice(0, 10);
    if (format === 'csv') {
      const csv =
        dataset === 'current'
          ? buildCurrentCsv()
          : [
              campaignCsv(campaignRows),
              creatorCsv(creatorRows),
            ].join('\r\n\r\n');
      const scope = dataset === 'current' ? tab : 'full';
      downloadCsv(`cpas-${scope}-report-${stamp}.csv`, csv);
      setNotice(
        dataset === 'current'
          ? `CSV export downloaded — ${currentTabLabel}.`
          : 'CSV export downloaded — full report with campaign and creator performance.',
      );
    } else {
      setPrintSections(dataset === 'current' ? [tab] : ['campaign', 'creator']);
      window.setTimeout(() => window.print(), 150);
      setNotice('PDF ready — choose "Save as PDF" in the print dialog to keep a copy.');
    }
    setExportOpen(false);
  };

  return (
    <>
      <div data-print-hide className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="page-rule" aria-hidden="true" />
            <h1 className="mt-1 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
              Reports
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-foreground-600">
              Campaign and creator performance, ready to export to CSV or PDF.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              icon="ri-calendar-schedule-line"
              disabled={!canExport}
              onClick={() =>
                setNotice('Scheduled reports become available once email delivery is connected.')
              }
            >
              Schedule email
            </Button>
            <Button
              icon="ri-download-2-line"
              disabled={!canExport}
              onClick={() => setExportOpen(true)}
            >
              Export
            </Button>
          </div>
        </div>

        {notice ? (
          <div className="mt-5 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-2.5">
            <span className="inline-flex items-center gap-2 text-sm text-accent-800">
              <i className="ri-checkbox-circle-line" />
              {notice}
            </span>
            <button
              type="button"
              onClick={() => setNotice('')}
              className="cursor-pointer text-accent-700 hover:text-accent-900"
              aria-label="Dismiss"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        ) : null}

        {!canExport ? (
          <div className="mt-5 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-600">
            <i className="ri-lock-line" />
            Your current role can view reports but not export them. Switch to Finance, Administrator
            or Client to download CSV and PDF reports.
          </div>
        ) : null}

        <div className="mt-6">
          <ReportToolbar
            tabs={reportTabs}
            tab={tab}
            onTab={setTab}
            period={period}
            onPeriod={(value) => setPeriod(value as ReportPeriod)}
            selectedMarkets={markets}
            onSetMarkets={setMarkets}
            periods={reportPeriods}
          />
        </div>

        <div className="mt-6">
          <ReportKpiRow kpis={kpis} />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center rounded-lg border border-background-200 bg-background-50 py-16 text-sm text-foreground-500">
                <i className="ri-loader-4-line mr-2 animate-spin" />
                Loading the report…
              </div>
            ) : loadError ? (
              <div className="rounded-lg border border-primary-200 bg-primary-50 py-12 text-center text-sm text-primary-800">
                <i className="ri-error-warning-line mr-1" />
                {loadError}
              </div>
            ) : null}

            {!loading && !loadError && tab === 'campaign' ? (
              <SectionCard
                title="Campaign performance"
                subtitle={`${campaignRows.length} campaigns in ${periodLabel.toLowerCase()}`}
                bodyClassName=""
                action={
                  <Button variant="ghost" size="sm" onClick={() => navigate('/campaigns')}>
                    View campaigns
                  </Button>
                }
              >
                <CampaignReportTable rows={campaignRows} />
              </SectionCard>
            ) : null}

            {!loading && !loadError && tab === 'creator' ? (
              <SectionCard
                title="Creator performance"
                subtitle={`${creatorRows.length} creators · ranked by engagement`}
                bodyClassName=""
                action={
                  <Button variant="ghost" size="sm" onClick={() => navigate('/discovery')}>
                    Open discovery
                  </Button>
                }
              >
                <CreatorPerformanceTable rows={creatorRows} />
              </SectionCard>
            ) : null}
          </div>

          <div>
            <ReportInsightsPanel
              insight={insight}
              canExport={canExport}
              onExport={() => setExportOpen(true)}
              // Production records no export history yet (docs/BACKEND_GAPS.md).
              exports={[]}
            />
          </div>
        </div>
      </div>

      <ExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        onExport={runExport}
        currentTabLabel={currentTabLabel}
      />

      <ReportPrintView
        sections={printSections}
        periodLabel={periodLabel}
        market={marketFilterLabel(markets)}
        generatedAt={generatedAt}
        campaignRows={campaignRows}
        creatorRows={creatorRows}
      />
    </>
  );
}